// constants/systemConstants.js

export const PUBLISHER_CLASSES = {
  ALL: ['AAA', 'AA', 'Indie', 'Hobbyist'],
  PREMIUM: ['AAA', 'AA'],
  INDIE: ['Indie', 'Hobbyist']
};

export const REFRESH_STATUS = {
  COMPLETE: 'COMPLETE',
  IN_PROGRESS: 'IN_PROGRESS', 
  FAILED: 'FAILED',
  INCOMPLETE: 'INCOMPLETE',
  PENDING: 'PENDING',
  UNKNOWN: 'UNKNOWN',
  ERROR: 'ERROR'
};

export const STATUS_COLORS = {
  green: {
    border: 'border-green-200',
    background: 'bg-green-50',
    text: 'text-green-800',
    button: 'bg-green-600 hover:bg-green-700',
    indicator: 'bg-green-100 text-green-700'
  },
  yellow: {
    border: 'border-yellow-200',
    background: 'bg-yellow-50', 
    text: 'text-yellow-800',
    button: 'bg-yellow-600 hover:bg-yellow-700',
    indicator: 'bg-yellow-100 text-yellow-700'
  },
  orange: {
    border: 'border-orange-200',
    background: 'bg-orange-50',
    text: 'text-orange-800', 
    button: 'bg-orange-600 hover:bg-orange-700',
    indicator: 'bg-orange-100 text-orange-700'
  },
  red: {
    border: 'border-red-200',
    background: 'bg-red-50',
    text: 'text-red-800',
    button: 'bg-red-600 hover:bg-red-700', 
    indicator: 'bg-red-100 text-red-700'
  },
  blue: {
    border: 'border-blue-200',
    background: 'bg-blue-50',
    text: 'text-blue-800',
    button: 'bg-blue-600 hover:bg-blue-700',
    indicator: 'bg-blue-100 text-blue-700'
  },
  gray: {
    border: 'border-gray-200',
    background: 'bg-gray-50',
    text: 'text-gray-800',
    button: 'bg-gray-600 hover:bg-gray-700',
    indicator: 'bg-gray-100 text-gray-700'
  }
};

export const STATUS_ICONS = {
  [REFRESH_STATUS.COMPLETE]: { icon: '✅', color: 'green' },
  [REFRESH_STATUS.IN_PROGRESS]: { icon: '⏳', color: 'blue' },
  [REFRESH_STATUS.FAILED]: { icon: '❌', color: 'red' },
  [REFRESH_STATUS.INCOMPLETE]: { icon: '⚠️', color: 'orange' },
  [REFRESH_STATUS.PENDING]: { icon: '⏸️', color: 'yellow' },
  [REFRESH_STATUS.UNKNOWN]: { icon: '❓', color: 'gray' },
  [REFRESH_STATUS.ERROR]: { icon: '💥', color: 'red' }
};

export const NOTIFICATION_CONFIG = {
  AUTO_DISMISS_DELAY: 5000,
  TYPES: {
    SUCCESS: 'success',
    ERROR: 'error', 
    INFO: 'info',
    WARNING: 'warning'
  }
};

export const DATASET_TYPES = {
  MARKET_STATS: 'marketStats',
  GENRE_ANALYTICS: 'genreAnalytics', 
  PUBLISHER_ANALYTICS: 'publisherAnalytics',
  TEMPORAL_ANALYSIS: 'temporalAnalysis',
  TRENDING_GAMES: 'trendingGames',
  REGIONAL_ANALYSIS: 'regionalAnalysis'
};

export const DATASET_DISPLAY_NAMES = {
  [DATASET_TYPES.MARKET_STATS]: 'Market Statistics',
  [DATASET_TYPES.GENRE_ANALYTICS]: 'Genre Analytics',
  [DATASET_TYPES.PUBLISHER_ANALYTICS]: 'Publisher Analytics', 
  [DATASET_TYPES.TEMPORAL_ANALYSIS]: 'Temporal Analysis',
  [DATASET_TYPES.TRENDING_GAMES]: 'Trending Games',
  [DATASET_TYPES.REGIONAL_ANALYSIS]: 'Regional Analysis'
};

export const DATA_FRESHNESS_THRESHOLDS = {
  FRESH_HOURS: 24,
  STALE_HOURS: 48,
  OLD_HOURS: 168, // 7 days
  CRITICAL_DAYS: 7
};

export const REFRESH_PRIORITY_COLORS = {
  HIGH: 'red',
  MEDIUM: 'yellow', 
  LOW: 'blue'
};

export const UI_POLLING_INTERVALS = {
  REFRESH_PROGRESS: 2000,
  SYSTEM_STATUS: 30000,
  NOTIFICATION_DISMISS: 5000
};

// Database and API Configuration Constants
export const DATABASE_LIMITS = {
  DEFAULT_PAGINATION: 50,
  MAX_PAGINATION: 1000,
  BATCH_SIZE: 100,
  CONNECTION_POOL_LIMIT: 10,
  QUERY_TIMEOUT_MS: 30000
};

export const API_TIMEOUTS = {
  SHORT: 5000,      // 5 seconds
  MEDIUM: 15000,    // 15 seconds  
  LONG: 30000,      // 30 seconds
  VERY_LONG: 60000, // 1 minute
  REQUEST_DELAY: 500 // Delay between API requests
};

export const REVENUE_THRESHOLDS = {
  SMALL: 1000,
  MEDIUM: 5000,
  LARGE: 10000,
  VERY_LARGE: 50000,
  MAJOR: 100000,
  BLOCKBUSTER: 500000
};

export const WISHLIST_THRESHOLDS = {
  MINIMAL: 10,
  LOW: 50,
  MEDIUM: 500,
  HIGH: 1000,
  VERY_HIGH: 5000,
  EXCEPTIONAL: 10000
};

export const CALCULATION_CONSTANTS = {
  MINIMUM_SAMPLE_SIZE: 10,
  STATISTICAL_CONFIDENCE: 0.95,
  OUTLIER_THRESHOLD: 2, // Standard deviations
  PERCENTAGE_PRECISION: 2 // Decimal places for percentages
};

// Button configuration constants
export const REFRESH_BUTTON_CONFIG = {
  SELECTED: {
    label: 'Refresh Selected',
    color: 'blue',
    icon: 'RefreshCw',
    description: 'Refresh only the publisher classes you have selected'
  },
  SMART: {
    label: 'Smart Refresh',
    color: 'green', 
    icon: 'CheckCircle',
    description: 'Let the system decide what needs to be refreshed based on data analysis'
  },
  FORCE: {
    label: 'Force Full Refresh',
    color: 'red',
    icon: 'AlertTriangle', 
    description: 'Force refresh all publisher classes and data (use with caution)'
  }
};

export const BUTTON_BASE_CLASSES = {
  PRIMARY: 'flex-1 flex items-center justify-center space-x-2 px-4 py-2 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed',
  COLORS: {
    blue: 'bg-blue-600 hover:bg-blue-700',
    green: 'bg-green-600 hover:bg-green-700',
    red: 'bg-red-600 hover:bg-red-700',
    yellow: 'bg-yellow-600 hover:bg-yellow-700',
    gray: 'bg-gray-600 hover:bg-gray-700'
  }
};

// Server Configuration Constants (shared between frontend and backend)
export const SERVER_DEFAULTS = {
  DEFAULT_PORT: 3001,
  DEFAULT_DB_CONNECTION_LIMIT: 10,
  DEFAULT_DB_QUEUE_LIMIT: 0,
  DEFAULT_REQUEST_DELAY: 500,
  DEFAULT_CACHE_SIZE: 2500
};

// Business Logic Constants
export const PUBLISHER_REVIEW_THRESHOLDS = {
  AAA: 0,
  AA: 0,
  Indie: 0,
  Hobbyist: 200,
  Other: 300
};

export const PUBLISHER_WISHLIST_THRESHOLDS = {
  AAA: 0,
  AA: 0,
  Indie: 10,
  Hobbyist: 50,
  Other: 50
};

export const PUBLISHER_GAME_LIMITS = {
  AAA: 999,
  AA: 100,
  Indie: 10,
  Hobbyist: 10,
  Other: 5
};
